import React, { useState, useEffect, useCallback } from 'react';
import { commissionAPI, usersAPI } from '../services/api';
import { useRole } from '../hooks/useRole';

const PAYOUT_COLORS = { pending: '#f59e0b', approved: '#3b82f6', paid: '#22c55e', on_hold: '#ef4444' };
const COMM_TYPE_LABELS = { personal: 'Personal', override: 'Override', team_bonus: 'Team Bonus', quarterly_bonus: 'Quarterly Bonus', salary: 'Salary', stipend: 'Stipend' };

function StatusBadge({ status }) {
  const c = PAYOUT_COLORS[status] || '#94a3b8';
  return <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: `${c}20`, color: c }}>{status?.replace('_', ' ')}</span>;
}

function Modal({ show, onClose, children }) {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 700, width: '95%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>✕</button>
        {children}
      </div>
    </div>
  );
}

function PayModal({ entry, onClose, onSave }) {
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  if (!entry) return null;
  const handlePay = async () => {
    setSaving(true);
    try { await commissionAPI.markEntryPaid(entry._id, { payment_reference: ref, payout_notes: notes }); onSave(); }
    catch { } finally { setSaving(false); }
  };
  return (
    <Modal show={!!entry} onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginTop: 0, marginBottom: 20 }}>Mark as Paid — ₹{(entry.commission_amount || 0).toLocaleString('en-IN')}</h2>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Payment Reference</label>
      <input value={ref} onChange={e => setRef(e.target.value)} placeholder="UTR / Cheque No." style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 14, boxSizing: 'border-box' }} />
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Notes</label>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', resize: 'vertical', marginBottom: 20, boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button onClick={handlePay} disabled={saving} style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Mark Paid'}</button>
      </div>
    </Modal>
  );
}

function ConfigModal({ userId, users, onClose, onSave }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    commissionAPI.getConfig(userId)
      .then(r => setConfig(r.data.data))
      .catch(() => {
        const user = users.find(u => u._id === userId);
        setConfig({ user_id: userId, role: user?.sub_role?.toLowerCase() || 'intern' });
      })
      .finally(() => setLoading(false));
  }, [userId, users]);

  const upd = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try { await commissionAPI.upsertConfig(userId, config); onSave(); }
    catch { } finally { setSaving(false); }
  };

  if (loading || !config) return <Modal show={!!userId} onClose={onClose}><div style={{ textAlign: 'center', padding: 40 }}>Loading...</div></Modal>;

  const Field = ({ label, k, type = 'number' }) => (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <input type={type} value={config[k] ?? ''} onChange={e => upd(k, type === 'number' ? Number(e.target.value) : e.target.value)}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }} />
    </div>
  );

  return (
    <Modal show={!!userId} onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginTop: 0, marginBottom: 4 }}>Commission Config</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>{users.find(u => u._id === userId)?.name}</p>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Role</label>
        <select value={config.role || 'intern'} onChange={e => upd('role', e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
          {['intern', 'manager', 'rcm', 'svp'].map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
        </select>
      </div>

      <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 10, marginTop: 16 }}>Salary / Stipend</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        <Field label="Monthly Salary (₹)" k="monthly_salary" />
        <Field label="Monthly Stipend (₹)" k="monthly_stipend" />
        <Field label="Salary Accrual Threshold (%)" k="salary_accrual_threshold" />
      </div>

      <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 10 }}>Monthly Targets</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        <Field label="Target Enrollments" k="monthly_target_enrollments" />
        <Field label="Target MoUs" k="monthly_target_mous" />
        <Field label="Target Revenue (₹)" k="monthly_target_revenue" />
      </div>

      <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 10 }}>Personal Commission Rates (%)</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        <Field label="Enrollment %" k="commission_rate_enrollment" />
        <Field label="MoU %" k="commission_rate_mou" />
        <Field label="Franchise %" k="commission_rate_franchise" />
        <Field label="Corporate %" k="commission_rate_corporate" />
      </div>

      {config.role === 'intern' && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 10 }}>Flat Bonus</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 16 }}>
            <Field label="Flat Bonus Per Enrollment (₹)" k="flat_bonus_per_enrollment" />
          </div>
        </>
      )}

      {config.role !== 'intern' && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 10 }}>Override Commission Rates (%)</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
            <Field label="Override Enrollment %" k="override_rate_enrollment" />
            <Field label="Override MoU %" k="override_rate_mou" />
            <Field label="Override Franchise %" k="override_rate_franchise" />
          </div>
        </>
      )}

      <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 10 }}>Quarterly Bonus Tiers</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 4 }}>
        <Field label="Tier 1 Threshold %" k="quarterly_bonus_tier1_threshold" />
        <Field label="Tier 2 Threshold %" k="quarterly_bonus_tier2_threshold" />
        <Field label="Tier 3 Threshold %" k="quarterly_bonus_tier3_threshold" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        <Field label="Tier 1 Flat (₹)" k="quarterly_bonus_tier1_flat" />
        <Field label="Tier 2 Flat (₹)" k="quarterly_bonus_tier2_flat" />
        <Field label="Tier 3 Flat (₹)" k="quarterly_bonus_tier3_flat" />
        <Field label="Excess Rate %" k="quarterly_bonus_excess_rate" />
      </div>

      {config.role === 'manager' && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 10 }}>Team Bonus</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 16 }}>
            <Field label="Team Bonus On Target Hit (₹)" k="team_bonus_on_target" />
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Config'}</button>
      </div>
    </Modal>
  );
}

export default function CommissionPage() {
  const { isAdminOrAbove } = useRole();
  const [tab, setTab] = useState('summary');
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [payEntry, setPayEntry] = useState(null);
  const [configUserId, setConfigUserId] = useState(null);
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth() + 1);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [calculating, setCalculating] = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes, cRes, uRes] = await Promise.all([
        commissionAPI.getEntrySummary({ month: monthFilter, year: yearFilter }),
        commissionAPI.getEntries({ month: monthFilter, year: yearFilter, payout_status: 'pending' }),
        commissionAPI.getConfigs(),
        usersAPI.getAll({ is_active: true }),
      ]);
      setSummary(sRes.data.data || []);
      setEntries(eRes.data.data || []);
      setConfigs(cRes.data.data || []);
      setUsers(uRes.data.data || []);
    } catch { showToast('Failed to load commission data', 'error'); }
    finally { setLoading(false); }
  }, [monthFilter, yearFilter]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    try { await commissionAPI.approveEntry(id); showToast('Approved'); load(); }
    catch { showToast('Failed to approve', 'error'); }
  };

  const handleHold = async (id) => {
    try { await commissionAPI.holdEntry(id, { payout_notes: 'Held by admin' }); showToast('On hold'); load(); }
    catch { showToast('Failed', 'error'); }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await commissionAPI.calculateEntries({ month: monthFilter, year: yearFilter });
      showToast(`Calculated: ${res.data.data?.users_affected} users affected`);
      load();
    } catch { showToast('Calculation failed', 'error'); }
    finally { setCalculating(false); }
  };

  const TABS = [
    { key: 'summary', label: 'Team Summary' },
    ...(isAdminOrAbove ? [{ key: 'queue', label: 'Payout Queue' }] : []),
    { key: 'quarterly', label: 'Quarterly Bonus' },
    ...(isAdminOrAbove ? [{ key: 'settings', label: 'Commission Settings' }] : []),
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          borderRadius: 8, padding: '12px 20px', fontWeight: 600, fontSize: 14,
        }}>{toast.msg}</div>
      )}

      <PayModal entry={payEntry} onClose={() => setPayEntry(null)} onSave={() => { setPayEntry(null); load(); showToast('Marked paid'); }} />
      <ConfigModal userId={configUserId} users={users} onClose={() => setConfigUserId(null)} onSave={() => { setConfigUserId(null); load(); showToast('Config saved'); }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>Commissions</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={monthFilter} onChange={e => setMonthFilter(Number(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
            {[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{new Date(2024, i).toLocaleString('en-IN', { month: 'long' })}</option>)}
          </select>
          <select value={yearFilter} onChange={e => setYearFilter(Number(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handleCalculate} disabled={calculating}
            style={{ background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            {calculating ? 'Calculating...' : 'Recalculate'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '10px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
              background: tab === t.key ? '#fff' : 'transparent',
              color: tab === t.key ? '#6366f1' : '#64748b',
              boxShadow: tab === t.key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            }}>{t.label}</button>
        ))}
      </div>

      {/* Tab 1 — Team Summary */}
      {tab === 'summary' && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {summary.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No commission data for this period. Click Recalculate to generate.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Team Member', 'Role', 'Personal', 'Override', 'Bonus', 'Salary', 'Total', 'Payout', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((s, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1e293b' }}>{s.user?.name || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#64748b' }}>{s.user?.role || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>₹{(s.personal || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}>₹{(s.override || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}>₹{(s.bonus || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}>₹{(s.salary || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#6366f1' }}>₹{(s.total || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {s.entries?.length > 0 && <StatusBadge status={s.entries[0].payout_status} />}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {s.entries?.filter(e => e.payout_status === 'pending').slice(0, 1).map(e => (
                          <React.Fragment key={e._id}>
                            <button onClick={() => handleApprove(e._id)}
                              style={{ background: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Approve</button>
                            <button onClick={() => setPayEntry(e)}
                              style={{ background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Pay</button>
                          </React.Fragment>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab 2 — Payout Queue */}
      {tab === 'queue' && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No pending entries</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Team Member', 'Type', 'Base', 'Rate', 'Amount', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1e293b' }}>{e.user_id?.name || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#64748b' }}>{COMM_TYPE_LABELS[e.commission_type] || e.commission_type}</td>
                    <td style={{ padding: '12px 14px' }}>₹{(e.base_amount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', color: '#64748b' }}>{e.rate || 0}%</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#6366f1' }}>₹{(e.commission_amount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}><StatusBadge status={e.payout_status} /></td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {e.payout_status === 'pending' && (
                          <button onClick={() => handleApprove(e._id)}
                            style={{ background: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Approve</button>
                        )}
                        {e.payout_status === 'approved' && (
                          <button onClick={() => setPayEntry(e)}
                            style={{ background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Mark Paid</button>
                        )}
                        <button onClick={() => handleHold(e._id)}
                          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Hold</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab 3 — Quarterly Bonus */}
      {tab === 'quarterly' && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>Quarterly bonus entries are generated automatically on the 2nd of each quarter. Use Recalculate to trigger manually for testing.</p>
          {summary.filter(s => s.bonus > 0).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No quarterly bonuses calculated for this period.</div>
          ) : (
            summary.filter(s => s.bonus > 0).map((s, i) => (
              <div key={i} style={{ padding: '16px 20px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{s.user?.name}</div>
                <div style={{ color: '#16a34a', fontSize: 20, fontWeight: 700, marginTop: 4 }}>₹{(s.bonus || 0).toLocaleString('en-IN')}</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>Quarterly Bonus</div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Tab 4 — Commission Settings */}
      {tab === 'settings' && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Name', 'Role', 'Salary', 'Target Revenue', 'Enroll Rate', 'Override Rate', 'Last Updated', 'Edit'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.filter(u => !['super_admin'].includes(u.role)).map((u, i) => {
                const cfg = configs.find(c => String(c.user_id?._id || c.user_id) === String(u._id));
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1e293b' }}>{u.name}</td>
                    <td style={{ padding: '12px 14px', color: '#64748b' }}>{u.role} {u.sub_role ? `/ ${u.sub_role}` : ''}</td>
                    <td style={{ padding: '12px 14px' }}>{cfg ? `₹${(cfg.monthly_salary || cfg.monthly_stipend || 0).toLocaleString('en-IN')}` : '—'}</td>
                    <td style={{ padding: '12px 14px' }}>{cfg ? `₹${(cfg.monthly_target_revenue || 0).toLocaleString('en-IN')}` : '—'}</td>
                    <td style={{ padding: '12px 14px' }}>{cfg ? `${cfg.commission_rate_enrollment || 0}%` : '—'}</td>
                    <td style={{ padding: '12px 14px' }}>{cfg ? `${cfg.override_rate_enrollment || 0}%` : '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#64748b', fontSize: 12 }}>{cfg?.updated_at ? new Date(cfg.updated_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <button onClick={() => setConfigUserId(u._id)}
                        style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
